/**
 * **The auth pane's photograph, and the memory that makes the pane one surface across steps.**
 *
 * Sign-up draws a different photograph on every step (`shared/lib/authHeroJourney.ts`): an arrival,
 * then preparation, then work, then the wind-down. Each change is a cross-fade.
 *
 * ── Why this component exists at all ────────────────────────────────────────────────────────
 * Every step of sign-up mounts its own `AuthShell` — the steps are different component types, so
 * React swaps rather than reuses, which is the whole mechanism `AuthShell`'s own entrance depends
 * on. That means this element is mounted fresh on every step, and a
 * mounted-fresh element has no idea what the pane was showing a moment ago. Left alone it would
 * either fade in on EVERY step — the photograph blinking on the way from screen 2 to 3, and again
 * on „Zmień" back to 2 — or never fade at all.
 *
 * So the memory is MODULE level: the last photograph actually painted into this pane, surviving the
 * remounts because it is not part of any component's state. The pane is one physical surface across
 * the flow even though the React tree under it is rebuilt each step, and this variable is that fact
 * written down. `resetHeroPhotoMemory` is that memory's one seam: `HeroPhoto.test.tsx` drives the
 * three cases it exists for, and would otherwise leak one case into the next.
 *
 * **The fade happens only where the picture actually changes.** Same photograph as last time —
 * which is „Mam trenera", standing on the role step's own beat, and „Zmień" back to the form — and there
 * is one plain `<img>` and no animation at all. First arrival likewise: a hero that fades up on a
 * cold load is a screen loading by moving, which this app does not do.
 *
 * ── Why no `AnimatePresence` ────────────────────────────────────────────────────────────────
 * There is no exit to orchestrate. The outgoing photograph is rendered UNDER the incoming one at
 * full strength and never animates — the fade happens entirely on the element on top, and the one
 * below is removed the frame it is completely covered, which no eye can see. `AnimatePresence`
 * would add a presence tree, a key and an `exit` state to express "leave it exactly where it is",
 * and it would still have to be mounted BEFORE the swap to run an exit at all, which the remount
 * forbids. One `motion.img`, one `onAnimationComplete`, and the old frame is gone.
 *
 * ── The one inline style in this file ───────────────────────────────────────────────────────
 * `object-position` is per-photograph DATA — where the face sits, whether the bar may leave the
 * frame — decided by looking at each image and carried with it in the table. A stylesheet cannot
 * hold seven answers to one property without seven classes named after seven files, and Stylelint's
 * ban is on design LITERALS, which a crop is not. `AuthShell.module.css` carries the same carve-out
 * in prose.
 */

import { useEffect, useState } from 'react';
import { motion } from 'motion/react';

import { screenMotion, useScreenMotionPreference } from '../design-system/motion';
import type { AuthHeroPhoto } from '../lib/authHeroJourney';

import styles from './HeroPhoto.module.css';

/**
 * The last photograph this pane painted, across every remount of it.
 *
 * `null` means the pane has not been on screen in this document yet, which is a first arrival and
 * has nothing to fade from.
 */
let lastPainted: AuthHeroPhoto | null = null;

/** For tests, which would otherwise carry one case's last photograph into the next. */
export function resetHeroPhotoMemory(): void {
  lastPainted = null;
}

interface HeroPhotoProps {
  readonly photo: AuthHeroPhoto;
}

/**
 * Mount-scoped by design: the caller keys it on `photo.src`, so a changed photograph is always a
 * fresh mount and the outgoing frame is read at exactly the moment the change happens.
 */
export function HeroPhoto({ photo }: HeroPhotoProps) {
  const reducedMotion = useScreenMotionPreference();
  const [outgoing, setOutgoing] = useState<AuthHeroPhoto | null>(() =>
    lastPainted !== null && lastPainted.src !== photo.src ? lastPainted : null,
  );

  useEffect(() => {
    lastPainted = photo;
  }, [photo]);

  if (outgoing === null) return <HeroFrame photo={photo} />;

  const swap = screenMotion('photoSwap', { reducedMotion });

  return (
    <>
      {/* The frame being left. Static, and wearing its OWN crop: the two photographs are cropped
          differently, and re-cropping the outgoing one to the incoming one's answer would shift the
          picture underneath the fade. */}
      <HeroFrame photo={outgoing} />

      <motion.img
        className={styles.image}
        src={photo.src}
        alt=""
        decoding="async"
        style={{ objectPosition: photo.position }}
        initial={swap.initial}
        animate={swap.animate}
        transition={swap.transition}
        onAnimationComplete={() => {
          setOutgoing(null);
        }}
      />
    </>
  );
}

/** One photograph, cropped as its own entry in the table says. Decorative — the pane is `aria-hidden`. */
function HeroFrame({ photo }: HeroPhotoProps) {
  return (
    <img
      className={styles.image}
      src={photo.src}
      alt=""
      decoding="async"
      style={{ objectPosition: photo.position }}
    />
  );
}
